import * as Sentry from "@sentry/nuxt";
import { AuthError } from "aws-amplify/auth";

import CallToActionToast from "~/components/base/feedback/BaseCallToActionToast.vue";

export default defineNuxtPlugin({
  name: "authenticateWithLiverpool",
  parallel: true,
  dependsOn: ["setInitialAuthState"],
  setup: async () => {
    if (!getIsHostLiverpool()) return;

    const auth = useAuth();
    if (auth?.isLoggedIn.value) return;

    const lfcLoginCookie = useCookie<LfcLoginCookie>("lfcLogin");
    const username = lfcLoginCookie.value?.id; // we are using the lfc_id as the username so that if they change their email address on the lfc-side, we don't have login issues
    const email = lfcLoginCookie.value?.username;
    if (!username || !email || !lfcLoginCookie.value?.dob) return;

    const isUserAuthenticatedOnLiverpool =
      await getIsUserAuthenticatedOnLiverpool();

    if (!isUserAuthenticatedOnLiverpool) {
      // the user is not authenticated on Liverpool FC, let them just continue as an anonymous user
      return;
    }

    const toast = useToast();

    const { differenceInYears, parse } = await import("date-fns");

    const dateOfBirth = parse(
      lfcLoginCookie.value.dob,
      "yyyy-MM-ddxxxxx",
      new Date(),
    );

    if (differenceInYears(new Date(), dateOfBirth) < 18) {
      toast.error(
        "We're sorry, but you need to be at least 18 years old to log in to the Liverpool FC Official Auction Store.",
      );

      return;
    }

    try {
      await auth.loginWith({
        strategy: "passwordless",
        options: {
          username,
        },
      });
    } catch (e) {
      try {
        if (!(e instanceof AuthError) || e.name !== "UserNotFoundException") {
          throw e;
        }

        await auth.register({
          email: username,
          password: generateRandomPassword(32),
          userAttributes: {
            email,
            "custom:lfc_id": lfcLoginCookie.value.id,
          },
        });
      } catch (e) {
        Sentry.captureException(e);
        toast.error({
          component: CallToActionToast,
          props: {
            htmlMessage:
              'We encountered an issue while trying to log you in. Please try again and if the problem persists, <a href="https://support.liverpool.mws.com" class="underline">contact support</a> for further help.',
          },
        });
      }
    }
  },
});
